import { getLeaderboardMetricText, getRankingsDisplay, isLeaderboardUpcoming } from 'fitbeat.gym-core';
import { IUserLeaderboardViewModel } from 'fitbeat.models';
import React, { FunctionComponent } from 'react';
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { LeaderboardOverview } from '../../SummaryDashboard/components/LeaderboardOverview';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';
import { LeaderboardRanking } from './LeaderboardRanking';

interface IProps {
  leaderboard: IUserLeaderboardViewModel;
  summary: boolean;
  t: any;
  navigation: any;
  teamMembersOverview?: boolean;
  onPress?: () => void;
  onTeamPress?: (team: string) => void;
  onModalUserScorePress?: (userProfileId: string, userName: string) => void;
  isFeatured: boolean;
  showRankings: boolean;
  showJoinLeaderboardTile: boolean;
  isMyCurrentTeam?: boolean;
}

export const LeaderboardTile: FunctionComponent<IProps> = ({
  leaderboard,
  t,
  summary,
  onPress,
  onTeamPress,
  onModalUserScorePress,
  teamMembersOverview = false,
  isFeatured,
  showRankings,
  showJoinLeaderboardTile,
  navigation,
  isMyCurrentTeam,
}) => {
  const isTeamLeaderboard = !!onTeamPress;
  const newLeaderboard = leaderboard.metric.includes('weighted');

  const rankings = teamMembersOverview
    ? leaderboard.rankings
    : getRankingsDisplay(leaderboard, summary);

  const userScorePress = (userProfileId: string, userName: string) => {
    if (navigation && navigation.navigate) {
      const team = navigation?.state?.params?.team;
      navigation.navigate('LeaderboardUserBreakdown', {
        userProfileId,
        leaderboardId: leaderboard.leaderboardId,
        startDate: leaderboard.startDate,
        endDate: leaderboard.endDate,
        t,
        isFeatured,
        leaderboard,
        userName,
        team,
      });
    }
  };
  let onUserScorePress = onModalUserScorePress;
  const onUserScorePressNotInTeam = onModalUserScorePress || userScorePress;

  if (!isMyCurrentTeam) {
    onUserScorePress = undefined;
  } else if (onUserScorePress === undefined) {
    onUserScorePress = userScorePress;
  }

  return (
    <TouchableOpacity style={styles.container} onPress={onPress} disabled={!onPress}>
      <View>
        <LeaderboardOverview
          userLeaderboardData={leaderboard}
          t={t}
          showIcons={false}
          showDivider
          allowPress={!!onPress}
          teamMembersOverview={teamMembersOverview}
          isFeatured={isFeatured}
          showJoinLeaderboardTile={showJoinLeaderboardTile}
          isTeamLeaderboard={isTeamLeaderboard}
          userBreakdown={undefined}
        />
      </View>
      {showRankings
        && (
          <View style={styles.rankingsContainer}>
            {newLeaderboard
              && (
                <View style={styles.row}>
                  <View style={styles.leftContainer}>
                    <Text style={[styles.superScriptMuted, { marginHorizontal: defaultScale(10) }]}>#</Text>
                    <Text style={[styles.superScriptMuted, { marginLeft: defaultScale(5) }]}>
                      { isTeamLeaderboard ? t('tracking:teamColumnHeader') : t('tracking:teamMemberColumnHeader') }
                    </Text>
                  </View>
                  <View style={styles.loadContainer}>
                    <Text style={styles.superScriptMuted}>{t('tracking:scoreColumnHeader')}</Text>
                  </View>
                </View>
              )}
            { rankings.map((ranking, index) => {
              const overrideIsMyCurrentTeam = !isMyCurrentTeam && ranking.isMine;

              return (
                <View key={ranking.rank}>
                  <LeaderboardRanking
                    ranking={ranking}
                    isMine={ranking.isMine}
                    isFirstItem={index === 0}
                    showEllipseBottom={ranking.showEllipseBottom}
                    onTeamPress={onTeamPress}
                    metricAbbreviation={getLeaderboardMetricText(leaderboard.metric).abbreviation}
                    showPositionIndicator={!isLeaderboardUpcoming(leaderboard.startDate)}
                    onUserScorePress={overrideIsMyCurrentTeam ? onUserScorePressNotInTeam : onUserScorePress}
                  />
                </View>
              );
            })}
          </View>
        )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.WHITE,
    marginBottom: defaultScale(10),
  },
  rankingsContainer: {
    paddingHorizontal: defaultScale(20),
    paddingBottom: defaultScale(20),
  },
  row: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: defaultScale(2),
    marginVertical: defaultScale(6),
  },
  leftContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  loadContainer: {
    alignItems: 'baseline',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    paddingRight: defaultScale(10),
  },
  superScriptMuted: {
    ...Theme.textStyle.small,
    color: Theme.GREY,
    fontFamily: Theme.Weight500,
  },
});
